import React from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import Link from 'next/link'
import { styled, keyframes, css } from 'styled-components'
import useIsMobile from '@/util/useIsMobile'
import { FREE } from '@/util/constants'
import { addSizeToUrl } from '@/util/imgHelpers'

import HomePageImage from '../HomePageImage'
import LockDisplay from '../LockDisplay'

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.5s infinite linear;
`

const SkeletonGrid = () => (
  <LookbookGrid>
    {[1, 2, 3, 4, 5, 6].map(i => (
      <SkeletonBlock
        key={i}
        style={{ paddingTop: i % 3 === 0 ? '120%' : '100%' }}
      />
    ))}
  </LookbookGrid>
)

// --- Grid ---
const LookbookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;

  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2px;
  }
`

// --- Standard cell (tall + normal) ---
const Cell = styled.div`
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: #1a1a1a;

  ${props => props.$span === 'tall' && css`
    grid-row: span 2;
    @media (max-width: 800px) {
      grid-row: span 2;
    }
  `}

  img {
    transition: transform 0.6s ease, opacity 0.5s ease;
  }

  &:hover img {
    transform: scale(1.06);
  }

  &:hover > div:last-child {
    opacity: 1;
  }
`

const CellImage = styled.div`
  position: relative;
  width: 100%;
  padding-top: ${props => props.$ratio || '100%'};

  img {
    position: absolute;
    inset: 0;
  }
`

const CellOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.75) 0%,
    rgba(0, 0, 0, 0.15) 40%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity 0.4s ease;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 24px;
  z-index: 2;

  @media (max-width: 800px) {
    padding: 16px;
    opacity: 1;
    background: linear-gradient(
      to top,
      rgba(0, 0, 0, 0.6) 0%,
      transparent 50%
    );
  }
`

const CellTitle = styled.h3`
  font-family: Soehne-Kraftig;
  font-size: ${props => props.$large ? 'clamp(18px, 2.5vw, 28px)' : 'clamp(14px, 1.5vw, 18px)'};
  color: white;
  margin: 0 0 6px;
  line-height: 1.2;
  letter-spacing: -0.01em;

  @media (max-width: 800px) {
    font-size: 14px;
  }
`

const CellCreator = styled.span`
  font-family: Soehne-Kraftig;
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #87a84a;

  @media (max-width: 800px) {
    font-size: 9px;
  }
`

const CellBadge = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 3;

  @media (max-width: 800px) {
    top: 10px;
    left: 10px;
  }
`

// --- Wide split cell (single blurred bg, text + image on top) ---
const WideCell = styled.div`
  grid-column: span 2;
  display: flex;
  overflow: hidden;
  cursor: pointer;
  background: #181818;
  position: relative;

  ${props => props.$flip && css`
    flex-direction: row-reverse;
  `}

  @media (max-width: 800px) {
    grid-column: span 2;
    flex-direction: column-reverse;
  }

  img {
    transition: transform 0.6s ease, opacity 0.5s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }
`

const WideCellBg = styled.div`
  position: absolute;
  inset: -30px;
  background-image: url(${props => props.$src});
  background-size: cover;
  background-position: center;
  filter: blur(50px) brightness(0.35) saturate(1.4);
  z-index: 0;
  pointer-events: none;
`

const WideImageSide = styled.div`
  flex: 1.2;
  position: relative;
  overflow: hidden;
  min-height: 480px;
  z-index: 1;

  @media (max-width: 800px) {
    min-height: 360px;
    flex: none;
  }

  img {
    position: absolute;
    inset: 0;
    object-fit: contain !important;
    object-position: center !important;
  }
`

const WideTextSide = styled.div`
  flex: 0.8;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 40px 36px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 24px 20px;
  }
`

const WideTagline = styled.span`
  font-family: Soehne-Kraftig;
  font-size: 10px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #87a84a;
  margin-bottom: 16px;
`

const WideTitle = styled.h3`
  font-family: Soehne-Kraftig;
  font-size: clamp(20px, 3vw, 32px);
  color: white;
  margin: 0 0 14px;
  line-height: 1.15;
  letter-spacing: -0.02em;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const WideExcerpt = styled.p`
  font-family: Soehne-Leicht;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 800px) {
    -webkit-line-clamp: 2;
    font-size: 13px;
  }
`

const WideBadge = styled.div`
  margin-bottom: 12px;
`

const GridOuter = styled.div`
  padding-bottom: 80px;

  @media (max-width: 800px) {
    padding-bottom: 60px;
  }
`

// --- Header cell (single column, spans 2 rows to sit beside the first wide post) ---
const HeaderCell = styled.div`
  grid-column: span 3;
  display: flex;
  align-items: stretch;
  background: #fafafa;
  padding: 14px 24px;

  @media (max-width: 800px) {
    grid-column: span 2;
    padding: 12px 16px;
  }
`

const MarkerAccent = styled.div`
  width: 3px;
  align-self: stretch;
  background: #546433;
  border-radius: 2px;
  flex-shrink: 0;
  margin-right: 16px;

  @media (max-width: 800px) {
    margin-right: 12px;
  }
`

const HeaderInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: 16px;
`

const HeaderTagline = styled.span`
  font-family: Soehne-Kraftig;
  font-size: 9px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #546433;
  margin-bottom: 4px;
`

const HeaderTitle = styled.h1`
  font-family: Soehne-Kraftig;
  font-size: 22px;
  letter-spacing: -0.03em;
  color: #181818;
  margin: 0;
  line-height: 1;
`

const HeaderSubtitle = styled.p`
  font-family: Soehne-Leicht;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.3);
  margin: 2px 0 0;
  line-height: 1.3;
`

const TabList = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`

const TabDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #546433;
  flex-shrink: 0;
`

const TabItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  font-family: Soehne-Kraftig;
  font-size: 9px;
  letter-spacing: 2px;
  text-transform: uppercase;
  cursor: pointer;
  color: ${props => (props.$active ? '#181818' : 'rgba(0, 0, 0, 0.3)')};
  background: ${props => (props.$active ? 'rgba(0, 0, 0, 0.05)' : 'transparent')};
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    color: ${props => (props.$active ? '#181818' : 'rgba(0, 0, 0, 0.5)')};
    background: rgba(0, 0, 0, 0.03);
  }
`

// Layout pattern — header spans full row, first post is wide, then 8-post cycle
const getCellLayout = (index) => {
  // First post: wide
  if (index === 0) return { type: 'wide', flip: false }
  // Next two normal
  if (index === 1) return { type: 'normal', ratio: '110%' }
  if (index === 2) return { type: 'normal', ratio: '100%' }

  // From index 3 onward, standard 8-post cycle
  const adjusted = index - 3
  const pos = adjusted % 8
  switch (pos) {
    case 0: return { type: 'tall', ratio: '200%' }
    case 1: return { type: 'normal', ratio: '110%' }
    case 2: return { type: 'normal', ratio: '100%' }
    case 3: return { type: 'wide', flip: true }
    case 4: return { type: 'tall', ratio: '200%' }
    case 5: return { type: 'normal', ratio: '100%' }
    case 6: return { type: 'normal', ratio: '110%' }
    case 7: return { type: 'wide', flip: false }
    default: return { type: 'normal', ratio: '100%' }
  }
}

const getMobileLayout = (index) => {
  const pos = index % 6
  switch (pos) {
    case 0: return { type: 'wide', flip: false }
    case 1: return { type: 'normal', ratio: '130%' }
    case 2: return { type: 'normal', ratio: '130%' }
    case 3: return { type: 'normal', ratio: '130%' }
    case 4: return { type: 'normal', ratio: '130%' }
    case 5: return { type: 'wide', flip: true }
    default: return { type: 'normal', ratio: '130%' }
  }
}

const WideCellComponent = ({ post, locked, isStandard, flip, isMobile }) => {
  const imgSrc = addSizeToUrl(post.coverImg, post.coverImgTitle)

  return (
    <Link passHref href={`/discover/${post.id}`}>
      <WideCell as="a" $flip={flip} style={{ textDecoration: 'none' }}>
        <WideCellBg $src={imgSrc} />
        <WideTextSide>
          <WideTagline>{post.authorUsername}</WideTagline>
          {locked && (
            <WideBadge>
              <LockDisplay
                subscriptionRequired={post?.subscriptionRequired}
                isStandard={isStandard}
                tag={`${post.uploadCount} IMAGES`}
                lockIconSize={14}
              />
            </WideBadge>
          )}
          <WideTitle>{post.title}</WideTitle>
          {post.description && (
            <WideExcerpt>{post.description}</WideExcerpt>
          )}
        </WideTextSide>
        <WideImageSide>
          <HomePageImage
            isMobile={isMobile}
            src={imgSrc}
            alt={post.title}
            className="w-full h-full block"
          />
        </WideImageSide>
      </WideCell>
    </Link>
  )
}

const renderStandardCell = (post, locked, isStandard, layout, isMobile) => (
  <Cell $span={layout.type === 'tall' ? 'tall' : null}>
    <CellImage $ratio={layout.ratio}>
      <HomePageImage
        isMobile={isMobile}
        src={addSizeToUrl(post.coverImg, post.coverImgTitle)}
        alt={post.title}
        className="w-full h-full object-cover object-center block"
      />
    </CellImage>
    {locked && (
      <CellBadge>
        <LockDisplay
          subscriptionRequired={post?.subscriptionRequired}
          isStandard={isStandard}
          tag={`${post.uploadCount} IMAGES`}
          lockIconSize={14}
        />
      </CellBadge>
    )}
    <CellOverlay>
      <CellTitle $large={layout.type === 'tall'}>
        {post.title}
      </CellTitle>
      <CellCreator>{post.authorUsername}</CellCreator>
    </CellOverlay>
  </Cell>
)

export const PostList = ({
  posts,
  unlockedList,
  loadMore,
  hasMore = false,
  isLoading = false,
  tabs = [],
  activeTab,
  onTabChange,
}) => {
  const isMobile = useIsMobile()

  const lockedPost = (post) => {
    if (post.postType === FREE) return false
    return !unlockedList?.includes(post.id)
  }

  const headerCell = (
    <HeaderCell key="header">
      <MarkerAccent />
      <HeaderInner>
        <div>
          <HeaderTagline>Beyond the Pines</HeaderTagline>
          <HeaderTitle>Factory</HeaderTitle>
          <HeaderSubtitle>
            Exclusive visual content from our creators
          </HeaderSubtitle>
        </div>
        {tabs.length > 1 && (
          <TabList>
            {tabs.map(tab => (
              <TabItem
                key={tab}
                $active={tab === activeTab}
                onClick={() => onTabChange(tab)}
              >
                {tab === activeTab && <TabDot />}
                {tab.toUpperCase()}
              </TabItem>
            ))}
          </TabList>
        )}
      </HeaderInner>
    </HeaderCell>
  )

  return (
    <GridOuter>
      {isLoading && posts.length === 0 ? (
        <SkeletonGrid />
      ) : (
        <InfiniteScroll
          dataLength={posts.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<SkeletonGrid />}
          style={{ overflow: 'visible' }}
        >
          <LookbookGrid>
            {headerCell}
            {posts.map((post, index) => {
              const layout = isMobile
                ? getMobileLayout(index)
                : getCellLayout(index)
              const locked = lockedPost(post)
              const isStandard = post?.subscriptionRequired === 'Standard'

              if (layout.type === 'wide') {
                return (
                  <WideCellComponent
                    key={post.id || index}
                    post={post}
                    locked={locked}
                    isStandard={isStandard}
                    flip={layout.flip}
                    isMobile={isMobile}
                  />
                )
              }

              return (
                <Link
                  key={post.id || index}
                  passHref
                  href={`/discover/${post.id}`}
                >
                  {renderStandardCell(post, locked, isStandard, layout, isMobile)}
                </Link>
              )
            })}
          </LookbookGrid>
        </InfiniteScroll>
      )}
    </GridOuter>
  )
}
